import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Star, ThumbsUp, BookOpen, Headphones, ChevronLeft, ChevronRight, EyeOff, Eye, AlertCircle } from "lucide-react";
import { fetchBookFeedback, toggleFeedbackVisibility } from "@/api/userBookFeedback";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface FeedbackDrawerProps {
  open: boolean;
  onClose: () => void;
  bookId: number;
  bookTitle: string;
}

const FeedbackDrawer = ({ open, onClose, bookId, bookTitle }: FeedbackDrawerProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [ratingFilter, setRatingFilter] = useState("all");
  const [recommendationFilter, setRecommendationFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [showHideDialog, setShowHideDialog] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<number | null>(null);
  const [hideReason, setHideReason] = useState("");
  const pageSize = 10;
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: feedbackData, isLoading } = useQuery({
    queryKey: ["book-feedback-all", bookId, currentPage, ratingFilter, recommendationFilter, sortBy],
    queryFn: () =>
      fetchBookFeedback(
        bookId,
        { page: currentPage, pageSize },
        { rating: ratingFilter, recommendation: recommendationFilter, sortBy, showHidden: true } // Admin always sees all
      ),
    enabled: open,
  });

  // Mutation for hiding/unhiding feedback
  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ feedbackId, isHidden, reason }: { feedbackId: number; isHidden: boolean; reason?: string }) => {
      return toggleFeedbackVisibility(feedbackId, isHidden, reason, user?.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["book-feedback-all", bookId] });
      queryClient.invalidateQueries({ queryKey: ["book-feedback", bookId] });
      toast({
        title: "Feedback updated",
        description: "Feedback visibility has been updated successfully.",
      });
      setShowHideDialog(false);
      setHideReason("");
      setSelectedFeedback(null);
    },
    onError: (error) => {
      console.error("Error updating feedback visibility:", error);
      toast({
        title: "Error",
        description: "Failed to update feedback visibility.",
        variant: "destructive",
      });
    },
  });

  const handleHide = (feedbackId: number) => {
    setSelectedFeedback(feedbackId);
    setShowHideDialog(true);
  };

  const handleUnhide = (feedbackId: number) => {
    toggleVisibilityMutation.mutate({ feedbackId, isHidden: false });
  };

  const confirmHide = () => {
    if (selectedFeedback) {
      toggleVisibilityMutation.mutate({
        feedbackId: selectedFeedback,
        isHidden: true,
        reason: hideReason || undefined,
      });
    }
  };

  const quickHideReasons = ["Spam", "Inappropriate content", "Off-topic", "Abusive language"];

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? "text-yellow-500 fill-yellow-500"
                : "text-gray-300 dark:text-gray-600"
            }`}
          />
        ))}
      </div>
    );
  };

  const feedbacks = feedbackData?.data || [];
  const totalFeedbacks = feedbackData?.total || 0;
  const totalPages = feedbackData?.totalPages || 1;

  // Calculate rating distribution
  const ratingDistribution = feedbacks.reduce((acc, feedback) => {
    if (feedback.overall_rating) {
      acc[feedback.overall_rating] = (acc[feedback.overall_rating] || 0) + 1;
    }
    return acc;
  }, {} as Record<number, number>);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>User Feedback</SheetTitle>
          <SheetDescription>
            {totalFeedbacks} {totalFeedbacks === 1 ? "review" : "reviews"} for "{bookTitle}"
          </SheetDescription>
        </SheetHeader>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger>
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
              <SelectItem value="highest">Highest Rated</SelectItem>
              <SelectItem value="lowest">Lowest Rated</SelectItem>
            </SelectContent>
          </Select>

          <Select value={ratingFilter} onValueChange={setRatingFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by rating" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ratings</SelectItem>
              <SelectItem value="5">⭐⭐⭐⭐⭐ (5 stars)</SelectItem>
              <SelectItem value="4">⭐⭐⭐⭐ (4 stars)</SelectItem>
              <SelectItem value="3">⭐⭐⭐ (3 stars)</SelectItem>
              <SelectItem value="2">⭐⭐ (2 stars)</SelectItem>
              <SelectItem value="1">⭐ (1 star)</SelectItem>
            </SelectContent>
          </Select>

          <Select value={recommendationFilter} onValueChange={setRecommendationFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Recommendation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="yes">Recommends</SelectItem>
              <SelectItem value="no">Doesn't Recommend</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Feedback List */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="text-center py-12 border rounded-lg bg-muted/20">
            <Star className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              No feedback matches your filters.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedbacks.map((feedback) => (
              <div
                key={feedback.id}
                className={`border rounded-lg p-4 space-y-3 ${
                  feedback.is_hidden
                    ? "bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-900"
                    : "bg-card"
                }`}
              >
                {/* Hidden Badge */}
                {feedback.is_hidden && (
                  <div className="flex items-center gap-2 pb-2 border-b border-red-200 dark:border-red-900">
                    <Badge variant="destructive" className="gap-1">
                      <EyeOff className="h-3 w-3" />
                      Hidden from users
                    </Badge>
                    {feedback.hidden_reason && (
                      <span className="text-xs text-muted-foreground">
                        Reason: {feedback.hidden_reason}
                      </span>
                    )}
                  </div>
                )}

                {/* Header: User and Rating */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`font-medium truncate ${feedback.is_hidden ? "line-through opacity-60" : ""}`}>
                        {feedback.profiles?.name || "Anonymous User"}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDistanceToNow(new Date(feedback.created_at), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                    {feedback.overall_rating && renderStars(feedback.overall_rating)}
                  </div>
                  
                  {/* Moderation Controls */}
                  <div className="flex items-center gap-2">
                    {feedback.is_hidden ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnhide(feedback.id)}
                        disabled={toggleVisibilityMutation.isPending}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        Unhide
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleHide(feedback.id)}
                        disabled={toggleVisibilityMutation.isPending}
                      >
                        <EyeOff className="h-3.5 w-3.5 mr-1" />
                        Hide
                      </Button>
                    )}
                  </div>
                </div>

                {/* Review Text */}
                {feedback.optional_review && (
                  <p className={`text-sm ${feedback.is_hidden ? "line-through opacity-60" : ""}`}>
                    "{feedback.optional_review}"
                  </p>
                )}

                {/* Ratings Grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {feedback.content_rating && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <BookOpen className="h-4 w-4" />
                      <span>Content: {feedback.content_rating}/5</span>
                    </div>
                  )}
                  {feedback.audio_rating && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Headphones className="h-4 w-4" />
                      <span>Audio: {feedback.audio_rating}/5</span>
                    </div>
                  )}
                  {feedback.would_recommend !== null && (
                    <div className="flex items-center gap-2">
                      <ThumbsUp
                        className={`h-4 w-4 ${
                          feedback.would_recommend
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                        }`}
                      />
                      <span className="text-sm text-muted-foreground">
                        {feedback.would_recommend ? "Recommends" : "Doesn't recommend"}
                      </span>
                    </div>
                  )}
                  {feedback.completion_percentage !== null && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span className="text-sm">
                        Completion: {Math.round(feedback.completion_percentage)}%
                      </span>
                    </div>
                  )}
                </div>

                {/* User Email and Moderation Info (for admin reference) */}
                <div className="text-xs text-muted-foreground pt-2 border-t space-y-1">
                  <div>{feedback.profiles?.email}</div>
                  {feedback.is_hidden && feedback.hidden_at && (
                    <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                      <AlertCircle className="h-3 w-3" />
                      <span>
                        Hidden {formatDistanceToNow(new Date(feedback.hidden_at), { addSuffix: true })}
                        {feedback.hidden_by_profile && ` by ${feedback.hidden_by_profile.name || feedback.hidden_by_profile.email}`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </SheetContent>

      {/* Hide Reason Dialog */}
      <Dialog open={showHideDialog} onOpenChange={setShowHideDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hide Feedback</DialogTitle>
            <DialogDescription>
              This feedback will be hidden from the user frontend but will remain visible in the admin dashboard.
              Optionally provide a reason for hiding this feedback.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Quick reasons:</label>
              <div className="flex flex-wrap gap-2">
                {quickHideReasons.map((reason) => (
                  <Button
                    key={reason}
                    variant="outline"
                    size="sm"
                    onClick={() => setHideReason(reason)}
                    className={hideReason === reason ? "bg-primary text-primary-foreground" : ""}
                  >
                    {reason}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="hide-reason" className="text-sm font-medium">
                Custom reason (optional):
              </label>
              <Input
                id="hide-reason"
                placeholder="Enter a reason for hiding this feedback..."
                value={hideReason}
                onChange={(e) => setHideReason(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowHideDialog(false);
              setHideReason("");
              setSelectedFeedback(null);
            }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmHide}
              disabled={toggleVisibilityMutation.isPending}
            >
              {toggleVisibilityMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Hide Feedback
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
};

export default FeedbackDrawer;

